import React, { memo, useEffect, useState } from "react";

import Space from "antd/es/space";
import Tooltip from "antd/es/tooltip";
import { defineMessages, useIntl } from "react-intl";

import { RawFile } from "@mapmycustomers/shared/types/File";
import { Button, LoadingSpinner, Modal, Popconfirm } from "@mapmycustomers/ui";

import hasMimeType, { AUDIO_ANY, TEXT_ANY, VIDEO_ANY } from "@app/util/mimeType";

import canPreview from "./canPreview";
import styles from "./PreviewFile.module.scss";
import TextFilePreview from "./TextFilePreview";

const PdfFilePreview = React.lazy(() => import("./PdfFilePreview"));

const messages = defineMessages({
  cancelText: {
    id: "component.filePreview.delete.cancel",
    defaultMessage: "Cancel",
    description: "Cancel text",
  },
  closeText: {
    id: "component.filePreview.closeText",
    defaultMessage: "Close",
    description: "Close text",
  },
  deleteFile: {
    id: "component.filePreview.deleteFile",
    defaultMessage: "Delete file",
    description: "Delete file",
  },
  deleteFileTitle: {
    id: "component.filePreview.deleteFile.title",
    defaultMessage: "Delete file?",
    description: "Delete file title",
  },
  downloadFile: {
    id: "component.filePreview.downloadFile",
    defaultMessage: "Download file",
    description: "Download file",
  },
  noPreview: {
    id: "component.filePreview.noPreview",
    defaultMessage: "No preview available for files of type {contentType}",
    description: "No preview available",
  },
  okText: {
    id: "component.filePreview.okText",
    defaultMessage: "Yes",
    description: "Ok text",
  },
});

export interface PreviewFileProps {
  deleteTooltip?: string;
  disabled?: boolean;
  file: RawFile;
  loading?: boolean;
  onCancel: () => void;
  onDelete?: (file: RawFile) => void;
  onDownload?: (file: RawFile) => void;
  onFetchFilePreview?: (fileId: RawFile["id"]) => void;
  preview: Blob | undefined;
  previewId: RawFile["id"] | undefined;
}

const PreviewFile = ({
  deleteTooltip,
  disabled,
  file,
  loading,
  onCancel,
  onDelete,
  onDownload,
  onFetchFilePreview,
  preview,
  previewId,
}: PreviewFileProps) => {
  const intl = useIntl();

  useEffect(() => {
    if (!canPreview(file)) {
      return;
    }
    onFetchFilePreview?.(file.id);
  }, [file, onFetchFilePreview]);

  // Pre-create the blob URL once per (preview, file) pair and revoke on
  // cleanup. Calling URL.createObjectURL inline in the render branches
  // would leak a URL on every render of the modal.
  const [srcUrl, setSrcUrl] = useState<string | undefined>();
  useEffect(() => {
    if (!preview || previewId !== file.id) {
      setSrcUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(preview);
    setSrcUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [preview, previewId, file.id]);

  return (
    <Modal
      cancelText={intl.formatMessage(messages.closeText)}
      footer={
        <Space>
          {(!disabled || deleteTooltip) && (
            <Popconfirm
              cancelText={intl.formatMessage(messages.cancelText)}
              description={intl.formatMessage(messages.deleteFileTitle)}
              disabled={disabled}
              getPopupContainer={(trigger: HTMLElement) => trigger.parentElement!}
              key="deleteFile"
              okText={intl.formatMessage(messages.okText)}
              onConfirm={() => {
                onDelete?.(file);
              }}
            >
              <Tooltip title={deleteTooltip ?? null}>
                <Button danger disabled={disabled} key="delete">
                  {intl.formatMessage(messages.deleteFile)}
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
          <Button
            key="download"
            onClick={() => {
              onDownload?.(file);
            }}
            type="primary"
          >
            {intl.formatMessage(messages.downloadFile)}
          </Button>
        </Space>
      }
      onCancel={onCancel}
      open
      title={file.name}
      wrapClassName={styles.previewModal}
    >
      <div className={styles.preview}>
        {loading ? (
          <LoadingSpinner />
        ) : preview && previewId === file.id ? (
          (() => {
            switch (file.contentType) {
              case "image/png":
              case "image/jpg":
              case "image/jpeg":
              case "image/gif":
                return <img alt={file.name} className={styles.previewImage} src={srcUrl} />;
              case "application/pdf":
                return (
                  <React.Suspense fallback={<LoadingSpinner />}>
                    <PdfFilePreview file={preview} />
                  </React.Suspense>
                );
              default:
                if (hasMimeType(file, VIDEO_ANY)) {
                  return (
                    <video controls height="267" width="494">
                      <source src={srcUrl} type={file.contentType} />
                    </video>
                  );
                }
                if (hasMimeType(file, AUDIO_ANY)) {
                  return (
                    <audio controls>
                      <source src={srcUrl} type={file.contentType} />
                    </audio>
                  );
                }
                if (hasMimeType(file, TEXT_ANY)) {
                  return <TextFilePreview file={preview} />;
                }
                return null;
            }
          })()
        ) : (
          intl.formatMessage(messages.noPreview, { contentType: file.contentType })
        )}
      </div>
    </Modal>
  );
};

export default memo(PreviewFile);
